import {
  Configuration,
  Membership,
  MembershipsApi,
  UpdateMembershipDto,
} from '@sr-sdks/api-sdk-axios';
import {
  queryOptions,
  skipToken,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { configuration } from '../../configuration';
import axiosInstance, { bypassCacheConfig } from '../../utils/axiosInstance';
import { toApiError } from '../../utils/httpError';

/**
 * Query key factory for memberships
 */
export const membershipsQueryKeys = {
  all: ['memberships'] as const,
  lists: () => [...membershipsQueryKeys.all, 'list'] as const,
  single: (membershipId?: string) =>
    [...membershipsQueryKeys.all, 'single', { membershipId }] as const,
};

const GetMembershipsApi = () => {
  return new MembershipsApi(
    new Configuration(),
    configuration.WORKSPACES_API_BASE,
    axiosInstance,
  );
};

export interface UpdateMembershipParams {
  id: string;
  updateMembershipDto: UpdateMembershipDto;
}

export const fetchMembershipById = async (
  membershipId: string,
): Promise<Membership> => {
  try {
    const response = await GetMembershipsApi().membershipControllerFindOne(
      membershipId,
      bypassCacheConfig(),
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'membershipControllerFindOne',
      params: { membershipId },
    });
  }
};

export const updateMembership = async ({
  id,
  updateMembershipDto,
}: UpdateMembershipParams): Promise<Membership> => {
  try {
    const response = await GetMembershipsApi().membershipControllerUpdate(
      id,
      updateMembershipDto,
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'membershipControllerUpdate',
      params: { id, updateMembershipDto },
    });
  }
};

export const queryOptionsGetMembership = (membershipId?: string) => {
  return queryOptions({
    queryFn: membershipId ? () => fetchMembershipById(membershipId) : skipToken,
    queryKey: membershipsQueryKeys.single(membershipId),
  });
};

/**
 * Hook for updating membership
 */
export const useUpdateMembership = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMembership,
    onSuccess: (data, variables) => {
      // Update the specific membership in cache
      queryClient.setQueryData(membershipsQueryKeys.single(variables.id), data);

      // Invalidate the specific membership query to refetch from server
      queryClient.invalidateQueries({
        queryKey: membershipsQueryKeys.single(variables.id),
      });
    },
  });
};
